import { useMemo } from "react";

/**
 * MeshBackground
 * --------------
 * A self-contained SVG mesh/network overlay that sits behind the main app
 * content. The illustration is generated deterministically from a fixed
 * seed so it is stable across renders (and across users/sessions).
 *
 * - Renders a full-viewport overlay (`fixed inset-0`) with`pointer-events-none`.
 * - Uses`z-index: -10`so it stays behind any content above it.
 * - Stroke color is`#64748b`at very low opacity (subtle, premium feel).
 * - Optional slow floating animation (~30s) keeps the mesh feeling alive
 * without being distracting.
 */
export default function MeshBackground() {
 const { nodes, edges } = useMemo(() => buildMesh(), []);

 return (
 <div
 aria-hidden="true"className="mesh-bg pointer-events-none fixed inset-0 -z-10 overflow-hidden">
 <svg
 className="mesh-bg__svg h-full w-full"viewBox="0 0 1600 900"preserveAspectRatio="xMidYMid slice"xmlns="http://www.w3.org/2000/svg">
 <g className="mesh-bg__float"fill="none"stroke="#64748b"strokeWidth="0.8"strokeOpacity="0.04">
 {edges.map(([a, b], i) => {
 const na = nodes[a];
 const nb = nodes[b];
 if (!na || !nb) return null;
 return (
 <line
 key={`edge-${i}`}
 x1={na.x}
 y1={na.y}
 x2={nb.x}
 y2={nb.y}
 />
 );
 })}
 </g>
 <g className="mesh-bg__float mesh-bg__float--slow"fill="#64748b"fillOpacity="0.045"stroke="none">
 {nodes.map((n, i) => (
 <circle key={`node-${i}`} cx={n.x} cy={n.y} r={n.r} />
 ))}
 </g>
 </svg>
 </div>
 );
}

/* -----------------------------------------------------------
 * Deterministic mesh generation
 * --------------------------------------------------------- */

interface MeshNode {
 x: number;
 y: number;
 r: number;
}

type MeshEdge = [number, number];

const VIEW_W = 1600;
const VIEW_H = 900;
const NODE_COUNT = 56;
// Maximum distance between two nodes for an edge to be drawn.
const MAX_LINK_DISTANCE = 240;
// Maximum number of neighbours any single node can be linked to.
const MAX_LINKS_PER_NODE = 3;
// Mulberry32 — tiny seeded PRNG so the mesh is identical on every render.
const MULBERRY32_SEED = 0x5eed5aa5;

function mulberry32(seed: number): () => number {
 let a = seed >>> 0;
 return () => {
 a = (a + 0x6d2b79f5) >>> 0;
 let t = a;
 t = Math.imul(t ^ (t >>> 15), t | 1);
 t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
 return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
 };
}

function buildMesh(): { nodes: MeshNode[]; edges: MeshEdge[] } {
 const rand = mulberry32(MULBERRY32_SEED);
 const nodes: MeshNode[] = [];

 // Margin so nodes do not crowd the very edges of the viewport.
 const padX = 80;
 const padY = 80;

 for (let i = 0; i < NODE_COUNT; i++) {
 const x = padX + rand() * (VIEW_W - padX * 2);
 const y = padY + rand() * (VIEW_H - padY * 2);
 // Vary node radius slightly between 2 and 3.5 px.
 const r = 2 + rand() * 1.5;
 nodes.push({ x, y, r });
 }

 // For each node, sort all other nodes by distance and link to the
 // closest MAX_LINKS_PER_NODE that fall within MAX_LINK_DISTANCE.
 const linkCount = new Array<number>(nodes.length).fill(0);
 const edgeSet = new Set<string>();
 const edges: MeshEdge[] = [];

 const distance = (a: MeshNode, b: MeshNode): number => {
 const dx = a.x - b.x;
 const dy = a.y - b.y;
 return Math.sqrt(dx * dx + dy * dy);
 };

 for (let i = 0; i < nodes.length; i++) {
 const ordered = nodes
 .map((n, j) => ({ j, d: distance(nodes[i]!, n) }))
 .filter((entry) => entry.j !== i && entry.d <= MAX_LINK_DISTANCE)
 .sort((a, b) => a.d - b.d);

 for (const { j } of ordered) {
 if (linkCount[i]! >= MAX_LINKS_PER_NODE) break;
 if (linkCount[j]! >= MAX_LINKS_PER_NODE) continue;
 const key = i < j ?`${i}-${j}`:`${j}-${i}`;
 if (edgeSet.has(key)) continue;
 edgeSet.add(key);
 edges.push([i, j]);
 linkCount[i]! += 1;
 linkCount[j]! += 1;
 }
 }

 return { nodes, edges };
}
